/**
 * React Query hooks for the auth feature.
 *
 * ``useCurrentUser`` is the single source of truth for the signed-in
 * user across the app (guard, header menu, home-page greeting). It
 * intentionally does NOT retry on 401 — the 401 is valid auth-state
 * data ("not logged in"), not a transient failure.
 *
 * ``useSignOut`` mutates the session away and then clears the cache
 * and routes to ``/signin``.
 */

"use client"

import { useRouter } from "next/navigation"
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query"

import { ApiError } from "@/lib/api/client"
import { suppressSessionExpiryMeta } from "@/lib/auth/session-expiry"

import { fetchCurrentUser, signOut } from "./api"
import type { CurrentUser } from "./types"

export const currentUserKey = ["auth", "me"] as const

const FIVE_MINUTES_MS = 5 * 60 * 1000

export function useCurrentUser(): UseQueryResult<CurrentUser, ApiError> {
  return useQuery<CurrentUser, ApiError>({
    queryKey: currentUserKey,
    queryFn: ({ signal }) => fetchCurrentUser({ signal }),
    staleTime: FIVE_MINUTES_MS,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 401) {
        return false
      }
      return failureCount < 1
    },
  })
}

export function useSignOut(): UseMutationResult<void, ApiError, void> {
  const router = useRouter()
  const queryClient = useQueryClient()

  return useMutation<void, ApiError, void>({
    mutationFn: () => signOut(),
    // A deliberate logout whose POST 401s (session already dead) must not
    // trip the global re-auth prompt — onSettled routes to /signin either
    // way. See suppressSessionExpiryMeta.
    meta: suppressSessionExpiryMeta,
    onSettled: () => {
      queryClient.removeQueries({ queryKey: currentUserKey })
      router.replace("/signin")
    },
  })
}
